// Thin fetch helper for the chakra backend.
//
// Type SSOT: every response/request shape on this file is a re-export
// of a generated TypeScript schema in `api-schema.d.ts`, which is in
// turn generated from FastAPI's /openapi.json via
//   npm run gen-api-types
// run that whenever the backend Pydantic models change. There must
// not be a hand-maintained type for any payload that crosses the wire
// — if a field exists on the Python side and not here, we want a TS
// compile error, not a silently blank tile.
//
// Same-origin: both the Next.js dev server (:3000) and the production
// reverse proxy serve /api on the same origin as the page, so the
// SessionMiddleware cookie set by FastAPI is sent automatically. We
// don't store tokens client-side; the cookie IS the session.

import type { components } from "@/lib/api-schema";

type S = components["schemas"];

// Friendly re-export names — saves callers having to write
// `components["schemas"]["AccountOut"]` everywhere. Keep these aliases
// in lockstep with the Pydantic class names on the backend.
export type Account = S["AccountOut"];
export type Me = S["MeOut"];
export type RegisterOut = S["RegisterOut"];
export type ApproveOut = S["ApproveOut"];
export type SetGroupOut = S["SetGroupOut"];
export type AccountHeader = S["AccountHeaderOut"];
export type PnlByCycleRow = S["PnlByCycleRow"];
export type PnlByExpiryRow = S["PnlByExpiryRow"];
export type PnlDailyRow = S["PnlDailyRow"];
export type OpenPositionRow = S["OpenPositionRow"];
export type OpenPositionByUnderlyingRow = S["OpenPositionByUnderlyingRow"];
export type OpenByUnderlyingOut = S["OpenByUnderlyingOut"];
export type OpenMatrixOut = S["OpenMatrixOut"];
export type OpenMatrixRow = S["OpenMatrixRow"];
export type OpenMatrixAccount = S["OpenMatrixAccount"];
export type StaleBhavcopyInfo = S["StaleBhavcopyInfo"];
export type TradeRow = S["TradeRow"];
export type CyclePnlBreakdown = S["CyclePnlBreakdown"];
export type ExposureRow = S["ExposureRow"];
export type UnrealizedMtm = S["UnrealizedMtmOut"];
export type PerBrokerDealerRow = S["PerBrokerDealerRow"];
export type PortfolioSummaryRow = S["PortfolioSummaryRow"];
export type PortfolioSummaryMultitradeRow = S["PortfolioSummaryMultitradeRow"];
export type PortfolioDailyLatestRow = S["PortfolioDailyLatestRow"];
export type UnderlyingPnlRow = S["UnderlyingPnlRow"];
export type DailyUnderlyingPnlRow = S["DailyUnderlyingPnlRow"];
export type EspRow = S["EspRow"];
export type NiftyCycleRow = S["NiftyCycleRow"];
export type AddMarginIn = S["AddMarginIn"];
export type AddMarginOut = S["AddMarginOut"];
export type AccountFreshnessRow = S["AccountFreshnessRow"];
export type BhavcopyFreshnessRow = S["BhavcopyFreshnessRow"];
export type MvStatusRow = S["MvStatusRow"];
export type DataFreshness = S["DataFreshnessOut"];
export type RefreshAccountOut = S["RefreshAccountOut"];
export type RefreshBhavcopyOut = S["RefreshBhavcopyOut"];
export type RefreshMvsOut = S["RefreshMvsOut"];
export type RefreshMvsRow = S["RefreshMvsRow"];
export type PitchbookInstitutional = S["PitchbookInstitutionalOut"];
export type PbSummary = S["PbSummary"];
export type PbDaily = S["PbDaily"];
export type PbMonthly = S["PbMonthly"];
export type PbDrawdown = S["PbDrawdown"];
export type PbUniverse = S["PbUniverse"];
export type PbCycle = S["PbCycleOut"];

export class HttpError extends Error {
  constructor(public status: number, public body: string) {
    super(`HTTP ${status}: ${body}`);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      // CSRF defense-in-depth: a custom header a cross-site <form> can't
      // set without a CORS preflight the backend never grants. Backend
      // requires it on cookie-authenticated mutating /api requests. Pairs
      // with the SameSite=Lax session cookie (the primary defense).
      "X-Requested-With": "chakra-web",
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    const body = await r.text();
    throw new HttpError(r.status, body);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

export const api = {
  login: (email: string, password: string) =>
    call<void>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => call<void>("/api/auth/logout", { method: "POST" }),
  me: () => call<Me>("/api/auth/me"),
  register: (
    email: string,
    password: string,
    extra?: { mobile_phone?: string; dob?: string },
  ) =>
    call<RegisterOut>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, ...(extra ?? {}) }),
    }),
  approve: (userId: number) =>
    call<ApproveOut>(`/api/auth/approve/${userId}`, { method: "POST" }),
  forgot: (email: string) =>
    call<void>("/api/auth/forgot", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  reset: (token: string, newPassword: string) =>
    call<void>("/api/auth/reset", {
      method: "POST",
      body: JSON.stringify({ token, new_password: newPassword }),
    }),
  setGroup: (userId: number, userGroup: string) =>
    call<SetGroupOut>(`/api/auth/set-group/${userId}`, {
      method: "POST",
      body: JSON.stringify({ user_group: userGroup }),
    }),
  updateMe: (patch: { mobile_phone?: string | null; dob?: string | null }) =>
    call<Me>("/api/auth/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  // Self-service password change (chakra_dashboard#125). Server
  // verifies `current_password` via bcrypt, then swaps in the new
  // hash. 204 on success; 400 with `detail` on any policy violation
  // (wrong current password, too short, same as current).
  changePassword: (body: { current_password: string; new_password: string }) =>
    call<void>("/api/auth/password", {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  accounts: () => call<Account[]>("/api/accounts"),
  header: (id: number) => call<AccountHeader>(`/api/accounts/${id}`),
  pnlCycle: (id: number) => call<PnlByCycleRow[]>(`/api/accounts/${id}/pnl/cycle`),
  pnlExpiry: (id: number) => call<PnlByExpiryRow[]>(`/api/accounts/${id}/pnl/expiry`),
  pnlDaily: (id: number, from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs}` : "";
    return call<PnlDailyRow[]>(`/api/accounts/${id}/pnl/daily${suffix}`);
  },
  openByUnderlying: (id: number) =>
    call<OpenByUnderlyingOut>(
      `/api/accounts/${id}/positions/open/by-underlying`,
    ),
  // Cross-account open-lots matrix — signed net delta lots per underlying,
  // one column per accessible account. Whole-scope; no account id.
  openMatrix: () => call<OpenMatrixOut>("/api/positions/open-matrix"),
  // account_id (string key) -> live constituent desk codes this cycle, for
  // aggregate accounts only (issue #178).
  aggregateCodes: () =>
    call<Record<string, string[]>>("/api/accounts/aggregate-codes"),
  unrealizedMtm: (id: number) =>
    call<UnrealizedMtm>(`/api/accounts/${id}/positions/unrealized-mtm`),
  openPositions: (id: number, underlying?: string) => {
    const qs = underlying ? `?underlying=${encodeURIComponent(underlying)}` : "";
    return call<OpenPositionRow[]>(`/api/accounts/${id}/positions/open${qs}`);
  },
  tradesInCycle: (id: number, underlying: string) =>
    call<TradeRow[]>(`/api/accounts/${id}/positions/${encodeURIComponent(underlying)}/trades`),
  allTradesInCycle: (
    id: number,
    filter?:
      | { cycle_label: string }
      | { exchange: string; expiry_month: string },
  ) => {
    const qs = new URLSearchParams();
    if (filter && "cycle_label" in filter) qs.set("cycle_label", filter.cycle_label);
    if (filter && "exchange" in filter) {
      qs.set("exchange", filter.exchange);
      qs.set("expiry_month", filter.expiry_month);
    }
    const suffix = qs.toString() ? `?${qs}` : "";
    return call<TradeRow[]>(`/api/accounts/${id}/trades${suffix}`);
  },
  cycleUnderlyings: (id: number, cycle_label: string) =>
    call<UnderlyingPnlRow[]>(
      `/api/accounts/${id}/pnl/cycles/${encodeURIComponent(cycle_label)}/underlyings`,
    ),
  dailyUnderlyings: (id: number, as_of_date: string) =>
    call<DailyUnderlyingPnlRow[]>(
      `/api/accounts/${id}/pnl/daily/${encodeURIComponent(as_of_date)}/underlyings`,
    ),
  expiryUnderlyings: (id: number, exchange: string, expiry_month: string) =>
    call<UnderlyingPnlRow[]>(
      `/api/accounts/${id}/pnl/expiries/${encodeURIComponent(exchange)}/${encodeURIComponent(expiry_month)}/underlyings`,
    ),
  espByDate: (id: number, expiry_date: string) =>
    call<EspRow[]>(
      `/api/accounts/${id}/pnl/expiries/${encodeURIComponent(expiry_date)}`,
    ),
  cyclePnlBreakdown: (id: number, underlying: string) =>
    call<CyclePnlBreakdown>(`/api/accounts/${id}/positions/${encodeURIComponent(underlying)}/cycle-pnl`),
  exposure: (id: number) => call<ExposureRow>(`/api/accounts/${id}/exposure`),
  perBrokerDealer: (from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    const suffix = qs.toString() ? `?${qs}` : "";
    return call<PerBrokerDealerRow[]>(`/api/reports/per-broker-dealer${suffix}`);
  },
  portfolioSummary: (activeOnly = true) => {
    const suffix = activeOnly ? "" : "?active_only=false";
    return call<PortfolioSummaryRow[]>(`/api/reports/portfolio-summary${suffix}`);
  },
  portfolioDailyLatest: (activeOnly = true) => {
    const suffix = activeOnly ? "" : "?active_only=false";
    return call<PortfolioDailyLatestRow[]>(`/api/reports/portfolio-daily-latest${suffix}`);
  },
  portfolioSummaryMultitrade: (activeOnly = true) => {
    const suffix = activeOnly ? "" : "?active_only=false";
    return call<PortfolioSummaryMultitradeRow[]>(`/api/reports/portfolio-summary-multitrade${suffix}`);
  },
  portfolioSummaryKanhoji: (activeOnly = true) => {
    const suffix = activeOnly ? "" : "?active_only=false";
    return call<PortfolioSummaryMultitradeRow[]>(`/api/reports/portfolio-summary-kanhoji${suffix}`);
  },
  portfolioSummaryOthers: (activeOnly = true) => {
    const suffix = activeOnly ? "" : "?active_only=false";
    return call<PortfolioSummaryMultitradeRow[]>(`/api/reports/portfolio-summary-others${suffix}`);
  },
  portfolioSummaryCombined: (activeOnly = true) => {
    const suffix = activeOnly ? "" : "?active_only=false";
    return call<PortfolioSummaryMultitradeRow[]>(`/api/reports/portfolio-summary-combined${suffix}`);
  },
  niftyCycles: () => call<NiftyCycleRow[]>("/api/admin-tools/nifty-cycles"),
  dataFreshness: () => call<DataFreshness>("/api/admin-tools/data-freshness"),
  refreshAccountMultitrade: (accountId: number) =>
    call<RefreshAccountOut>(`/api/admin-tools/refresh/account-multitrade/${accountId}`, { method: "POST" }),
  refreshAccountKanhoji: (accountId: number) =>
    call<RefreshAccountOut>(`/api/admin-tools/refresh/account-kanhoji/${accountId}`, { method: "POST" }),
  refreshMvs: () =>
    call<RefreshMvsOut>("/api/admin-tools/refresh/mvs", { method: "POST" }),
  refreshBhavcopy: (exchange: string) =>
    call<RefreshBhavcopyOut>(`/api/admin-tools/refresh/bhavcopy/${exchange}`, { method: "POST" }),
  addMargin: (body: AddMarginIn) =>
    call<AddMarginOut>("/api/admin-tools/margins", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // Pitchbook (manager+). Institutional tear-sheet computed live from the
  // MVs — no cache, no AI. Account is anonymised server-side to `S{id}`.
  // `range` windows the sheet to a NIFTY-cycle span (from = cycle start,
  // to = cycle end/expiry); omit for full history.
  pitchbookInstitutional: (id: number, range?: { from?: string | null; to?: string | null }) => {
    const qs = new URLSearchParams();
    if (range?.from) qs.set("from", range.from);
    if (range?.to) qs.set("to", range.to);
    const suffix = qs.toString() ? `?${qs}` : "";
    return call<PitchbookInstitutional>(`/api/accounts/${id}/pitchbook/institutional${suffix}`);
  },
  // NIFTY expiry cycles the account spans — powers the From/To range picker.
  pitchbookCycles: (id: number) =>
    call<PbCycle[]>(`/api/accounts/${id}/pitchbook/cycles`),
  // Selector list — only accounts with P&L history (empty accounts excluded
  // so the tear-sheet never defaults onto one that 404s).
  pitchbookAccounts: () => call<Account[]>("/api/pitchbook/accounts"),
};
